import { useCallback } from 'react';
import { IInternalRegulationObligationReviewStatus } from '../../../graphql/generated';
import { REGULATION_TABS } from '../../../helpers';
import { useRegulationDetail } from '../../RegulationDetailProvider';
import { useRegulation } from '../../RegulationProvider';
import {
  IInternalRegulationObligationsQuery,
  useInternalRegulationObligationsQuery,
} from '../graphql/queries/internalRegulationObligations.generated';
import {
  createDocumentInstanceIdFilter,
  createObligationReviewStatusFilter,
  createRegulationIdFilter,
} from './filterConstants';

const PAGE_SIZE = 25;

export const useObligations = (
  statusFilter: IInternalRegulationObligationReviewStatus | null,
  onCompleted?: (data: IInternalRegulationObligationsQuery) => void,
) => {
  const { regulationId } = useRegulationDetail();
  const { activeDocument, activeRegulationTabId } = useRegulation();
  const statusGroupKey =
    activeRegulationTabId === REGULATION_TABS.REVIEW.id
      ? 'REGULATION_OBLIGATION_REVIEW_STATUS'
      : 'REGULATION_OBLIGATION_MAPPINGS_REVIEW_STATUS';

  const statusFilterGroup = statusFilter ? [createObligationReviewStatusFilter(statusFilter, statusGroupKey)] : [];

  const { data, loading, error, fetchMore } = useInternalRegulationObligationsQuery({
    variables: {
      first: PAGE_SIZE,
      input: {
        filters: {
          groups: [
            createRegulationIdFilter(regulationId),
            createDocumentInstanceIdFilter(activeDocument?.id ?? ''),
            ...statusFilterGroup,
          ],
        },
      },
    },
    context: {
      gateway: 'REG_MAPPING',
    },
    notifyOnNetworkStatusChange: true,
    skip: !regulationId || !activeDocument?.id,
    onCompleted,
  });

  const pageInfo = data?.internalRegulationObligationsByDocument?.pageInfo;

  const handleFetchMore = useCallback(() => {
    if (pageInfo?.hasNextPage && !loading) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
      });
    }
  }, [pageInfo, loading, fetchMore]);

  return {
    obligationsData: data?.internalRegulationObligationsByDocument.edges,
    loading,
    error,
    pageInfo,
    handleFetchMore,
  };
};
